import { t } from "ttag";
import { IDeviceHealthRecord, IUnitRef } from "../../models/DeviceHealth.types";

/** Record keys that represent a problem when the list is non-empty. */
export type ProblemListKey =
  | "controlUnitsWithoutService"
  | "serviceUnitsWithoutControl"
  | "controlUnitsNotInSystem"
  | "serviceUnitsNotInSystem"
  | "outdoorUnitsNotInSystem"
  | "disconnectedControlUnits"
  | "disconnectedServiceUnits"
  | "disconnectedOutdoorUnits";

export interface IProblemGroup {
  key: ProblemListKey;
  label: string;
  /** these lists carry a per-unit disconnect timestamp */
  withTimestamp?: boolean;
}

export const problemListKeys: ProblemListKey[] = [
  "controlUnitsWithoutService",
  "serviceUnitsWithoutControl",
  "controlUnitsNotInSystem",
  "serviceUnitsNotInSystem",
  "outdoorUnitsNotInSystem",
  "disconnectedControlUnits",
  "disconnectedServiceUnits",
  "disconnectedOutdoorUnits"
];

/**
 * The problem lists in display order, with their labels. Built as a function so
 * the strings are translated at render time rather than at import time.
 */
export const getProblemGroups = (): IProblemGroup[] => [
  {
    key: "controlUnitsWithoutService",
    label: t`Control units not associated to service units`
  },
  {
    key: "serviceUnitsWithoutControl",
    label: t`Service units not associated with control units`
  },
  {
    key: "controlUnitsNotInSystem",
    label: t`Control units not in a system`
  },
  {
    key: "serviceUnitsNotInSystem",
    label: t`Service units not in a system`
  },
  {
    key: "outdoorUnitsNotInSystem",
    label: t`Outdoor units not in a system`
  },
  {
    key: "disconnectedControlUnits",
    label: t`Disconnected control units`,
    withTimestamp: true
  },
  {
    key: "disconnectedServiceUnits",
    label: t`Disconnected service units`,
    withTimestamp: true
  },
  {
    key: "disconnectedOutdoorUnits",
    label: t`Disconnected outdoor units`,
    withTimestamp: true
  }
];

export const getProblemUnits = (record: IDeviceHealthRecord, key: ProblemListKey): IUnitRef[] =>
  record[key] || [];

export const isDeviceConnected = (record?: IDeviceHealthRecord | null): boolean =>
  // Treat a missing connectivity block as connected rather than inventing a fault.
  record?.deviceConnectivity?.isConnected !== false;

/**
 * Single source of truth for the red X in the table and the highlighting in the
 * popup, so the two can never disagree.
 *
 * Trusts the server's `hasIssues` when present - it applies the same rule, and
 * deferring to it keeps the indicator consistent with any server-side
 * "problems only" filtering. Rows snapshotted before that field existed fall
 * back to the local computation.
 */
export const hasHealthIssues = (record?: IDeviceHealthRecord | null): boolean => {
  if (!record) {
    return false;
  }

  if (typeof record.hasIssues === "boolean") {
    return record.hasIssues;
  }

  if (!isDeviceConnected(record)) {
    return true;
  }

  return problemListKeys.some((key) => getProblemUnits(record, key).length > 0);
};

/** Short "what's wrong" lines, used for the health indicator tooltip. */
export const getHealthIssueSummary = (record?: IDeviceHealthRecord | null): string[] => {
  if (!record) {
    return [];
  }

  const summary: string[] = [];

  if (!isDeviceConnected(record)) {
    summary.push(t`Device is disconnected`);
  }

  getProblemGroups().forEach(({ key, label }) => {
    const count = getProblemUnits(record, key).length;

    if (count > 0) {
      summary.push(`${label}: ${count}`);
    }
  });

  return summary;
};
